import { X, Sparkles, Zap } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { isNativeApp } from '../lib/platform'
import { BILLING_ENABLED } from '../config/features'

interface UpgradeBannerProps {
  variant?: 'desktop' | 'mobile'
}

export default function UpgradeBanner({ variant = 'desktop' }: UpgradeBannerProps) {
  // 無料モードではアップグレード訴求を表示しない（定数条件なのでフック規則には抵触しない）
  if (!BILLING_ENABLED) return null
  const { user } = useAuth()
  const [isVisible, setIsVisible] = useState(false)
  const [tier, setTier] = useState<string>('free')
  const [usage, setUsage] = useState({ projects: 0, todos: 0, storage: 0 })
  const [limits, setLimits] = useState({ projects: 2, todos: 20, storage: 10 })

  useEffect(() => {
    if (!user) return

    // Check if banner was dismissed in this session
    const dismissed = sessionStorage.getItem(`upgrade-banner-dismissed-${variant}`)
    if (dismissed) {
      setIsVisible(false)
      return
    }

    loadUserData()
  }, [user, variant])

  const loadUserData = async () => {
    if (!user) return

    try {
      // Get user profile and tier
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('subscription_tier')
        .eq('id', user.id)
        .single()

      const userTier = profile?.subscription_tier || 'free'
      setTier(userTier)

      // Don't show for Premium users
      if (userTier === 'premium') {
        setIsVisible(false)
        return
      }

      // Get usage limits
      const { data: limitsData } = await supabase
        .from('usage_limits')
        .select('max_projects, max_todos_per_project, max_file_storage_mb')
        .eq('subscription_tier', userTier)
        .single()

      const currentLimits = {
        projects: limitsData?.max_projects || 2,
        todos: limitsData?.max_todos_per_project || 20,
        storage: limitsData?.max_file_storage_mb || 10
      }

      setLimits(currentLimits)

      // Get current usage
      const { data: projects } = await supabase
        .from('projects')
        .select('id')
        .eq('user_id', user.id)

      const projectCount = projects?.length || 0

      // Get max todos in any project
      const { data: todoCounts } = await supabase
        .from('todos')
        .select('project_id')
        .eq('user_id', user.id)

      const todosByProject = todoCounts?.reduce((acc, todo) => {
        acc[todo.project_id] = (acc[todo.project_id] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      const maxTodos = Math.max(...Object.values(todosByProject || {}), 0)

      // Get storage usage
      const { data: storageData } = await supabase.rpc('get_user_storage_usage', {
        user_id_param: user.id
      })

      const storageMB = storageData?.[0]?.total_bytes
        ? Math.round(storageData[0].total_bytes / (1024 * 1024) * 10) / 10
        : 0

      setUsage({
        projects: projectCount,
        todos: maxTodos,
        storage: storageMB
      })

      // Determine visibility based on tier and usage
      if (userTier === 'free') {
        setIsVisible(true) // Always show for free users
      } else if (userTier === 'pro') {
        // Show if usage is over 80%
        const maxUsagePercent = Math.max(
          projectCount / currentLimits.projects,
          maxTodos / currentLimits.todos,
          storageMB / currentLimits.storage
        )
        setIsVisible(maxUsagePercent > 0.8)
      }
    } catch (error) {
      console.error('Error loading upgrade banner data:', error)
    }
  }

  const handleDismiss = () => {
    setIsVisible(false)
    sessionStorage.setItem(`upgrade-banner-dismissed-${variant}`, 'true')
  }

  // ネイティブアプリでは課金誘導UIを表示しない（Apple/Googleガイドライン対応）
  if (!isVisible || isNativeApp()) return null

  const getUsagePercent = () => {
    const projectPercent = (usage.projects / limits.projects) * 100
    const todoPercent = (usage.todos / limits.todos) * 100
    const storagePercent = (usage.storage / limits.storage) * 100
    return Math.max(projectPercent, todoPercent, storagePercent)
  }

  const usagePercent = getUsagePercent()
  const isWarning = usagePercent > 80

  const getMessage = () => {
    if (tier === 'free') {
      if (isWarning) {
        return {
          icon: <Zap className="h-5 w-5" />,
          text: '制限に近づいています！Proプランで最大100タスク/プロジェクト',
          bgColor: 'bg-gradient-to-r from-orange-500 to-red-500'
        }
      }
      return {
        icon: <Sparkles className="h-5 w-5" />,
        text: 'Proプランで100タスク/プロジェクト + 200MB ストレージ',
        bgColor: 'bg-gradient-to-r from-emerald-500 to-teal-500'
      }
    } else {
      // Pro user near limit
      return {
        icon: <Sparkles className="h-5 w-5" />,
        text: 'Premiumで300タスク/プロジェクト + 1GB ストレージ + 優先サポート',
        bgColor: 'bg-gradient-to-r from-purple-600 to-pink-600'
      }
    }
  }

  const message = getMessage()

  if (variant === 'mobile') {
    return (
      <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 ${message.bgColor} text-white shadow-lg`}>
        <div className="flex items-center justify-between px-3 py-2 pb-safe">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {message.icon}
            <span className="text-xs font-medium truncate">{message.text}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/pricing"
              className="px-3 py-1 bg-white text-gray-900 text-xs font-semibold rounded hover:bg-gray-100 transition-colors"
            >
              アップグレード
            </Link>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              aria-label="閉じる"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Desktop variant
  return (
    <div className={`hidden lg:block ${message.bgColor} text-white rounded-lg shadow-lg mb-4 overflow-hidden relative z-10`}>
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          {message.icon}
          <div>
            <p className="text-sm font-semibold">{message.text}</p>
            <p className="text-xs opacity-90 mt-0.5">
              月額{tier === 'free' ? '¥500' : '¥980'}で制限を大幅に拡張
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/pricing"
            className="px-6 py-2 bg-white text-gray-900 text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            プランを見る
          </Link>
          <button
            onClick={handleDismiss}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            aria-label="閉じる"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
